import React from "react";

// Fit-for-this-persona hero + "why this contact" strip. Pure-derive — no LLM
// calls, no backend. Shared by the Deep Research side panel (DeepResearch.js)
// and the Contact-page Deep Research tab (DeepResearchTabContent.js) so both
// surfaces show the same fit summary.

// ── Hero card: Fit-for-this-persona summary ──
export const ContactHeroCard = ({ researchData, sellerName }) => {
  const ui = researchData?.user_insights || {};
  const opportunities = Array.isArray(ui.selling_opportunities) ? ui.selling_opportunities : [];
  const starters = Array.isArray(ui.conversation_starters) ? ui.conversation_starters : [];
  const profile = researchData?.ai_insights || ui.structured_profile || {};

  // Derive fit score from existing signal density (no BE change).
  // 0-10 scale: weighted by high-urgency opps + total opps + starters present.
  const highUrgency = opportunities.filter(o => (o.urgency || "").toLowerCase() === "high").length;
  const medUrgency = opportunities.filter(o => (o.urgency || "").toLowerCase() === "medium").length;
  const raw = highUrgency * 3 + medUrgency * 2 + Math.min(opportunities.length, 3)
            + (starters.length > 0 ? 2 : 0);
  const fitScore = Math.min(10, Math.max(0, raw));
  const confidence = fitScore >= 7 ? "High" : fitScore >= 4 ? "Medium" : "Low";
  const confidenceColor = fitScore >= 7 ? "ctHero--high" : fitScore >= 4 ? "ctHero--med" : "ctHero--low";

  // 1-sentence "why" — pulls top opportunity headline if present, else falls
  // back to seniority+role hint from profile.
  const topOpp = opportunities[0] || {};
  const whyLine = topOpp.signal || topOpp.trigger_insight || topOpp.headline
    || (profile?.role && profile?.seniority
        ? `${profile.seniority} ${profile.role} — typical buyer for ${sellerName || "your offering"}.`
        : "Based on profile + recent signals.");

  return (
    <div className={`ctHero ${confidenceColor}`}>
      <div className="ctHero__scoreCol">
        <div className="ctHero__score">{fitScore}</div>
        <div className="ctHero__scoreMax">/ 10</div>
        <div className="ctHero__confidence">{confidence} Fit</div>
      </div>
      <div className="ctHero__body">
        <div className="ctHero__label">Fit for {sellerName ? <strong>{sellerName}</strong> : "this seller"}</div>
        <p className="ctHero__why">{whyLine}</p>
        <div className="ctHero__counts">
          <span className="ctHero__chip">
            <strong>{opportunities.length}</strong> selling signals
          </span>
          <span className="ctHero__chip">
            <strong>{starters.length}</strong> conversation starters
          </span>
        </div>
      </div>
    </div>
  );
};

// ── "Why this contact" badge strip — top 3 quick-take points ──
export const WhyThisContact = ({ researchData }) => {
  const ui = researchData?.user_insights || {};
  const opportunities = Array.isArray(ui.selling_opportunities) ? ui.selling_opportunities : [];
  // Top 3 opps by urgency; pull the cleanest 1-line label per item.
  const order = { high: 0, medium: 1, low: 2 };
  const ranked = [...opportunities].sort((a, b) =>
    (order[(a.urgency || "").toLowerCase()] ?? 99) - (order[(b.urgency || "").toLowerCase()] ?? 99));
  const top = ranked.slice(0, 3).map(o => {
    const text = o.signal || o.trigger_insight || o.headline || o.why_it_matters || "";
    return { text: String(text).trim(), urgency: (o.urgency || "").toLowerCase() };
  }).filter(t => t.text);

  if (top.length === 0) return null;
  return (
    <div className="ctWhy">
      <div className="ctWhy__label">WHY THIS CONTACT</div>
      <div className="ctWhy__items">
        {top.map((t, i) => (
          <div key={i} className={`ctWhy__item ctWhy__item--${t.urgency || "neutral"}`}>
            <span className="ctWhy__dot" />
            <span className="ctWhy__text">{t.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactHeroCard;
